import { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';
import { getUserPreferences } from '../services/api';

const DEFAULTS = {
  theme:                  'saffron',
  defaultPaymentMode:     '',
  tablePageSize:          25,
  defaultFinancialYearId: null,
};

const PreferencesContext = createContext({
  preferences: DEFAULTS,
  refreshPreferences: () => {},
});

export const usePreferences = () => useContext(PreferencesContext);

export const PreferencesProvider = ({ children }) => {
  const { user, activeDerasarId, visibleFinancialYears, setActiveFinancialYearId } = useAuth();
  const [preferences, setPreferences] = useState(DEFAULTS);
  const appliedKeyRef = useRef(null);

  const refreshPreferences = async () => {
    if (!user) { setPreferences(DEFAULTS); return; }
    try {
      const res = await getUserPreferences();
      const pref = res.data?.data || res.data || {};
      setPreferences({
        theme:                  pref.theme                  || DEFAULTS.theme,
        defaultPaymentMode:     pref.defaultPaymentMode     || DEFAULTS.defaultPaymentMode,
        tablePageSize:          pref.tablePageSize          ?? DEFAULTS.tablePageSize,
        defaultFinancialYearId: pref.defaultFinancialYearId || DEFAULTS.defaultFinancialYearId,
      });
    } catch { /* keep current */ }
  };

  // Load once on login / logout
  useEffect(() => {
    if (!user) { setPreferences(DEFAULTS); appliedKeyRef.current = null; return; }
    refreshPreferences();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Apply defaultFinancialYearId once per (derasar, fy) combo per session
  useEffect(() => {
    if (!preferences.defaultFinancialYearId || !activeDerasarId || !visibleFinancialYears.length) return;
    const key = `${activeDerasarId}:${preferences.defaultFinancialYearId}`;
    if (appliedKeyRef.current === key) return;
    appliedKeyRef.current = key;
    const match = visibleFinancialYears.find(
      (fy) => String(fy.id) === String(preferences.defaultFinancialYearId)
    );
    if (match) setActiveFinancialYearId(String(match.id));
  }, [preferences.defaultFinancialYearId, activeDerasarId, visibleFinancialYears, setActiveFinancialYearId]);

  return (
    <PreferencesContext.Provider value={{ preferences, refreshPreferences }}>
      {children}
    </PreferencesContext.Provider>
  );
};
